import { useCallback } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useActionContext } from './actionContext';
import { getShareActionConfig } from './shared/shareAction';
import type { ShareActionComponentProps } from './types';

type Options<CH extends ShareChannel = ShareChannel> = {
	trackingEventName: string;
	config: ShareConfig;
	getUrl: (channel: CH) => string;
	getShortUrl: (channel: CH) => string;
	getShareId?: (channel: CH) => string | undefined;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

function extractShareId(longUrl: string, shortUrl: string): string | undefined {
	if (longUrl === shortUrl) return undefined; // shortening failed
	try {
		return new URL(shortUrl).pathname.split('/').pop() || undefined;
	} catch {
		return undefined;
	}
}

type Result =
	| {
			type: 'function';
			action: () => Promise<boolean>;
	  }
	| {
			type: 'component';
			Component: (props: ShareActionComponentProps) => React.JSX.Element;
	  };

export function useGetShareActionConfig<CH extends ShareChannel = ShareChannel>({
	getUrl,
	getShortUrl,
	getShareId,
	getTrackingData,
	getContent,
	trackingEventName,
	config,
	onSuccess,
	onFailure,
}: Options<CH>): (channel: CH) => Result {
	const actionContext = useActionContext();
	const { utilityContext } = actionContext;
	return useCallback(
		(channel) => {
			const shareActionConfig = getShareActionConfig(channel);
			const longUrl = utilityContext.resolveUrl(getUrl(channel));
			const shortUrl = getShortUrl(channel);
			const shareId = getShareId ? getShareId(channel) : extractShareId(longUrl, shortUrl);
			const options = {
				url: shortUrl,
				trackingEventName,
				trackingData: {
					...getTrackingData(channel),
					...(shareId ? { generated_share_id: shareId } : {}),
				},
				content: {
					title: getContent(channel, 'title', { url: shortUrl }),
					body: getContent(channel, 'body', { url: shortUrl }),
					image: getContent(channel, 'image', { url: shortUrl }),
				},
				config,
			};
			if (shareActionConfig.type === 'function') {
				return {
					type: 'function' as const,
					action: async () => {
						const success = await shareActionConfig.action(options, actionContext);
						if (success) {
							void onSuccess?.(channel);
						} else {
							void onFailure?.(channel);
						}
						return success;
					},
				};
			}
			const { Component } = shareActionConfig;
			return {
				type: 'component' as const,
				Component: ({ onAfterAction, onBeforeAction, children }: ShareActionComponentProps) => {
					const onAfterActionDecorated = useCallback(
						(success: boolean) => {
							void onAfterAction?.(success);
							if (success) {
								void onSuccess?.(channel);
							} else {
								void onFailure?.(channel);
							}
						},
						[onAfterAction],
					);
					return (
						<Component options={options} onBeforeAction={onBeforeAction} onAfterAction={onAfterActionDecorated}>
							{children}
						</Component>
					);
				},
			};
		},
		[
			actionContext,
			utilityContext,
			getUrl,
			getShortUrl,
			getShareId,
			getTrackingData,
			getContent,
			onSuccess,
			onFailure,
			trackingEventName,
			config,
		],
	);
}
